import { useServices } from '@/app/providers';
import type { Notification } from '@yawi/domain';
import { useCallback, useEffect, useRef, useState } from 'react';

const POLLING_INTERVAL = 30000; // 30 seconds

interface UseNotificationsOptions {
  enablePolling?: boolean;
  limit?: number;
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const useNotifications = (options: UseNotificationsOptions = {}): UseNotificationsReturn => {
  const { enablePolling = true, limit = 10 } = options;
  const { notificationRepo, currentUser } = useServices();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifs, count] = await Promise.all([
        notificationRepo.getNotifications({ limit }),
        notificationRepo.getUnreadCount(),
      ]);

      if (isMountedRef.current) {
        setNotifications(notifs.data);
        setUnreadCount(count);
        setError(null);
      }
    } catch (e) {
      if (isMountedRef.current) {
        setError(e instanceof Error ? e.message : 'Error al cargar notificaciones');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [notificationRepo, limit]);

  const refresh = useCallback(async () => {
    setLoading(true);
    await fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await notificationRepo.markAsRead(id);
      // Update local state
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, status: 'READ' as const, readAt: new Date().toISOString() } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error('Error marking notification as read:', e);
    }
  }, [notificationRepo]);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationRepo.markAllAsRead();
      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, status: 'READ' as const, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (e) {
      console.error('Error marking all notifications as read:', e);
    }
  }, [notificationRepo]);

  // Initial fetch — solo cuando el usuario ya está autenticado
  useEffect(() => {
    if (!currentUser) return;
    isMountedRef.current = true;
    fetchNotifications();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchNotifications, currentUser]);

  // Polling — solo cuando el usuario ya está autenticado
  useEffect(() => {
    if (!enablePolling || !currentUser) return;

    const interval = setInterval(() => {
      notificationRepo.getUnreadCount().then(count => {
        if (isMountedRef.current) {
          setUnreadCount(count);
        }
      }).catch(() => {
        // Silenciar errores de polling — el apiClient maneja el refresh/logout
      });
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [enablePolling, notificationRepo, currentUser]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    refresh,
  };
};
